import { useEffect, useState } from 'react';
import Card from '../components/Card.jsx';
import Button from '../components/Button.jsx';
import Chip from '../components/Chip.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { useComputeCredit } from '../context/ComputeCreditContext.jsx';
import { billingApi } from '../lib/apiClient.js';
import { RATES, cpuRunsForCredits, cpuSavingsPct, planCreditLabel, topupRunLabel } from '../utils/pricing.js';
import { planCardClassName } from './plans.js';
import { universeCountLabel } from './plans.js';
import useUniverseSummary from '../hooks/useUniverseSummary.js';

/**
 * Pricing & billing — buy the Schedule-D tiers or top up compute credit.
 *
 * Checkout is provider-hosted (Stripe or Dodo, toggled server-side by
 * PAYMENT_GATEWAY): the backend creates a checkout session and returns a
 * `checkoutUrl`; we redirect the user there. On return we reconcile via
 * /api/billing/status/:orderId (which checks the gateway) and refresh the
 * wallet. No payment SDK ships in the browser.
 */
export default function Pricing() {
  const [plans, setPlans] = useState(null);
  const [topups, setTopups] = useState([]);
  const [gateway, setGateway] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const { wallet, refresh } = useComputeCredit();
  const { loading: authLoading } = useAuth();
  const universe = useUniverseSummary();

  useEffect(() => {
    if (authLoading) return; // wait for token mint before authed fetches
    billingApi
      .plans()
      .then((d) => {
        setPlans(d.plans || []);
        setTopups(d.topups || []);
        setGateway(d.gateway || '');
      })
      .catch(() => setError('Failed to load pricing.'));
    billingApi.orders().then((rows) => setHistory(Array.isArray(rows) ? rows : [])).catch(() => setHistory([]));
    // If we came back from the hosted checkout, reconcile the newest pending
    // order (the gateway/webhook grants the tier + credits) and refresh.
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('success') === '1') {
      setMessage('Payment received — applying your credits / plan…');
      pollPendingUntilApplied();
      window.history.replaceState({}, '', '/app/billing');
    } else if (qs.get('cancel') === '1') {
      setMessage('Checkout cancelled — no charge was made.');
      window.history.replaceState({}, '', '/app/billing');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, authLoading]);

  // Poll the most recent pending order's status until the provider webhook
  // reconciles it (grants tier/credit). Times out after ~12s.
  async function pollPendingUntilApplied() {
    const refreshOnce = async () => {
      refresh();
      billingApi.orders().then((rows) => setHistory(Array.isArray(rows) ? rows : [])).catch(() => {});
    };
    for (let i = 0; i < 6; i += 1) {
      try {
        const rows = await billingApi.orders();
        const pending = (rows || []).find((o) => o.status === 'pending' || o.status === 'payment_pending' || o.status === 'activated');
        if (!pending) {
          await refreshOnce();
          return;
        }
        if (pending.status === 'activated') {
          setMessage('Payment applied — your credits / plan are live.');
          await refreshOnce();
          return;
        }
        // Reconcile this order against the gateway (idempotent fulfil).
        await billingApi.status(pending.orderId).catch(() => {});
      } catch {
        /* keep polling */
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    await refreshOnce();
    setMessage((prev) => (prev || '').includes('applied') ? prev : 'Payment received — credits / plan will show once confirmed.');
  }

  async function checkout(planId, topupId) {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const out = await billingApi.checkout({ planId, topupId });
      if (!out?.checkoutUrl) {
        throw new Error(out?.gateway === 'dodo' ? 'Dodo checkout did not return a URL.' : 'Payment is not configured yet — add gateway credentials (see docs).');
      }
      setMessage('Redirecting to secure checkout…');
      // Let the message paint, then bounce to the provider-hosted page.
      setTimeout(() => {
        window.location.href = out.checkoutUrl;
      }, 300);
    } catch (err) {
      setError(err.message || 'Checkout failed.');
      setBusy(false);
    }
  }

  const myTier = wallet?.tier || 'payg';

  function showTopUps() {
    setMessage('PAYG is already your current plan. Choose a credit pack below to continue running backtests.');
    document.getElementById('top-ups')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <>
      <Card>
        <h1 className="heading" style={{ fontSize: 22 }}>
          Pricing &amp; billing
        </h1>
        <p className="aq-module-desc">
          {wallet ? (
            <>
              Current seat: <strong>{wallet.tier?.toUpperCase()}</strong> · balance{' '}
              <strong>${wallet.balanceUsd?.toFixed?.(2) ?? '—'}</strong>
            </>
          ) : (
            'Loading your seat…'
          )}
          {gateway && <span className="aq-history-mode"> · via {gateway}</span>}
        </p>
      </Card>

      {error && <div className="aq-auth-alert aq-auth-alert--error">{error}</div>}
      {message && <div className="aq-auth-alert aq-auth-alert--success">{message}</div>}

      {/* ── How compute credit works ───────────────────────────────────── */}
      <Card style={{ marginTop: 16 }}>
        <h3 className="heading" style={{ fontSize: 16, marginBottom: 8 }}>
          How compute credit works
        </h3>
        <div className="aq-credit-explain">
          <p>
            <strong>Every backtest or research run uses compute, and each run debits a small
            amount from your credit balance.</strong> A <em>credit</em> is $1 of prepaid compute.
            When your balance runs low, top up or upgrade — the Backtest Studio shows a banner
            and the top bar always shows your remaining balance.
          </p>
          <div className="aq-rate-table">
            <div className="aq-rate-row aq-rate-head">
              <span>Run type</span>
              <span>PAYG</span>
              <span>Analyst</span>
              <span>Pro</span>
            </div>
            <div className="aq-rate-row">
              <span>CPU run (backtest or ML)</span>
              <span>${RATES.payg.cpu.toFixed(2)}</span>
              <span>${RATES.analyst.cpu.toFixed(2)}</span>
              <span>${RATES.pro.cpu.toFixed(2)}</span>
            </div>
            <div className="aq-rate-row">
              <span>CUDA ML research</span>
              <span>${RATES.payg.gpu.toFixed(2)}</span>
              <span>${RATES.analyst.gpu.toFixed(2)}</span>
              <span>${RATES.pro.gpu.toFixed(2)}</span>
            </div>
          </div>
          <p className="aq-module-desc" style={{ marginTop: 8 }}>
            Example: a Pro CPU backtest costs <strong>$0.48</strong> — so $120 of monthly credit
            funds roughly <strong>250 runs</strong>. The same run on PAYG costs $0.80.
          </p>
        </div>
      </Card>

      {/* ── Plans ──────────────────────────────────────────────────────── */}
      <section style={{ marginTop: 20 }}>
        <h2 className="aq-section-title heading">
          Plans <span className="aq-module-desc">(best value for active research)</span>
        </h2>
        <div className="aq-grid aq-grid--3">
          <Card key="payg">
            <h3 className="heading" style={{ fontSize: 18 }}>
              PAYG
            </h3>
            <p className="aq-plan-price">No monthly fee · pay per run</p>
            <ul className="aq-plan-benefits">
              <li><strong>$0 / month</strong> — top up only what you use</li>
              <li>All backtest modes (rules, signals, MC, walk-forward, portfolio)</li>
              <li>Live MSA data + {universeCountLabel(universe?.total)} universe</li>
              <li>CPU runs at <strong>${RATES.payg.cpu.toFixed(2)}</strong> each</li>
              <li>GPU research (ML) at <strong>${RATES.payg.gpu.toFixed(2)}</strong> each</li>
            </ul>
            <Button
              variant="ghost"
              disabled={busy}
              onClick={myTier === 'payg' ? showTopUps : () => setMessage('Contact support to cancel an active subscription before switching to PAYG.')}
              className="aq-btn--block"
            >
              {myTier === 'payg' ? 'Current plan · Top up' : 'Switch to PAYG'}
            </Button>
          </Card>

          {(plans || []).map((p) => {
            const r = RATES[p.tier];
            const savings = cpuSavingsPct(p.tier);
            const isPopular = p.tier === 'analyst';
            return (
              <Card key={p.id} className={planCardClassName(isPopular)}>
                {isPopular && <span className="aq-plan-badge">Most popular</span>}
                <h3 className="heading" style={{ fontSize: 18 }}>
                  {p.label}
                  {myTier === p.tier && <Chip tone="gain" style={{ marginLeft: 8 }}>Current</Chip>}
                </h3>
                <p className="aq-plan-price">
                  ${p.priceUsd}/month · <strong>{planCreditLabel(p.tier, r.monthly)}</strong>
                </p>
                <ul className="aq-plan-benefits">
                  <li><strong>{r.monthly} compute credits every month</strong></li>
                  <li>CPU runs at <strong>${r.cpu.toFixed(2)}</strong> — {savings}% cheaper than PAYG</li>
                  <li>GPU research at <strong>${r.gpu.toFixed(2)}</strong> each</li>
                  {(p.benefits || []).filter((b) => !b.startsWith(`${r.monthly} compute credit`) && !b.startsWith('All backtest modes')).map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
                <Button onClick={() => checkout(p.id, undefined)} disabled={busy} className="aq-btn--block">
                  {myTier === p.tier ? 'Current plan' : `Subscribe — $${p.priceUsd}/mo`}
                </Button>
              </Card>
            );
          })}
        </div>
      </section>

      {/* ── Top ups (for PAYG / topping up) ────────────────────────────── */}
      <section id="top-ups" style={{ marginTop: 24 }}>
        <h2 className="aq-section-title heading">Top up compute credit</h2>
        <p className="aq-module-desc">One-time credit you can use anytime — the main way to keep going on PAYG, or to extend a plan month.</p>
        <div className="aq-grid aq-grid--3">
          {(topups || []).map((t) => (
            <Card key={t.id}>
              <h3 className="heading" style={{ fontSize: 16 }}>
                ${t.creditsUsd} credit
              </h3>
              <p className="aq-module-desc">{topupRunLabel(t.creditsUsd, t.priceUsd, myTier)}</p>
              <Button variant="secondary" onClick={() => checkout(undefined, t.id)} disabled={busy} className="aq-btn--block">
                Buy
              </Button>
            </Card>
          ))}
        </div>
      </section>

      <Card className="aq-history" style={{ marginTop: 24 }}>
        <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
          Your purchases
        </h3>
        {history.length === 0 ? (
          <p className="aq-module-desc">No purchases yet.</p>
        ) : (
          <div className="aq-history-list">
            {history.map((h) => (
              <div key={h.orderId} className="aq-history-row">
                <span className="aq-history-mode">{h.meta?.plan || h.meta?.topup || 'purchase'}</span>
                <span className="aq-history-date">{new Date(h.createdAt).toLocaleString()}</span>
                <Chip tone={h.status === 'activated' ? 'gain' : h.status === 'pending' ? 'warn' : 'loss'}>
                  {h.status}
                </Chip>
                <span className="aq-history-meta">${h.amountUsd?.toFixed?.(2) ?? '—'}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
